import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UIIcon, UILoading, UIModalService } from '@shared/ui';
import { LocalizationPipe } from '@shared/modules/localization';
import { NotificationsService } from '@pkp/services';
import { INotification, NotificationTypeEnum } from '@pkp/models';
import { Router } from '@angular/router';
import { NotificationCardComponent, NotificationModalComponent } from '@pkp/shared';
import { catchError, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MessagesService } from '@shared/modules/messages';

const NOTIFICATIONS_SCROLL_THRESHOLD = 48;
const NOTIFICATIONS_BADGE_MAX = 99;

@Component({
  selector: 'pkp-d-notifications-dropdown',
  standalone: true,
  templateUrl: './notifications-dropdown.component.html',
  styleUrls: ['./notifications-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UIIcon, UILoading, LocalizationPipe, NotificationCardComponent],
})
export class NotificationsDropdownComponent implements OnInit {
  private readonly notificationsService = inject(NotificationsService);
  private readonly modalService = inject(UIModalService);
  private readonly messagesService = inject(MessagesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly isOpen = signal(false);
  readonly notifications = this.notificationsService.notifications;
  readonly unreadCount = this.notificationsService.unreadCount;
  readonly total = this.notificationsService.total;
  readonly loading = this.notificationsService.loading;
  readonly badgeText = computed(() => {
    const count = this.unreadCount();
    return count > NOTIFICATIONS_BADGE_MAX ? `${NOTIFICATIONS_BADGE_MAX}+` : String(count);
  });

  ngOnInit(): void {
    this.loadNotifications();
  }

  toggle(event: Event): void {
    event.stopPropagation();
    this.isOpen.update(isOpen => !isOpen);
  }

  onListScroll(event: Event): void {
    const list = event.target as HTMLElement;
    if (list.scrollTop + list.clientHeight < list.scrollHeight - NOTIFICATIONS_SCROLL_THRESHOLD) {
      return;
    }
    this.notificationsService.loadMore().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  @HostListener('document:click')
  close(): void {
    this.isOpen.set(false);
  }

  onNotificationSelected(notification: INotification): void {
    if (!notification.is_read) {
      this.notificationsService
        .markAsRead(notification.id)
        .pipe(
          catchError((err: HttpErrorResponse) => {
            this.messagesService.showError(err.error?.message ?? err.message);
            return of(void 0);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    if (notification.type === NotificationTypeEnum.FeedbackAnswer && notification.entity_uuid) {
      this.close();
      this.router.navigate(['/desktop/main/feedback', notification.entity_uuid]).then();
      return;
    }

    this.modalService.create(NotificationModalComponent, notification).subscribe();
  }

  private loadNotifications(): void {
    this.notificationsService
      .getNotifications()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
